import { ComponentPropDefinitions, ComponentPropDefinitionUtil } from "../../base/ComponentProps";
import { PropType } from "../../base/PropType";
import { ImagePropDefinitions } from "../image/ImageProps";
import { PanelPropDefinitions, PanelProps } from "../panel/PanelProps";

export interface TabProps extends PanelProps {
    caption: string;
    closeable: boolean;
    displaysDividerBorder: boolean;
    headingMaxWidth: number;
    headingMinWidth: number;
    showTitleInScroll: boolean;
}

let tabPropDefs: ComponentPropDefinitions;

export class TabPropDefinitions {
    public static getDefinitions(): ComponentPropDefinitions {
        if (tabPropDefs == null) {
            tabPropDefs = {
                ...PanelPropDefinitions.cloneDefs(),
                ...ImagePropDefinitions.getContainedImagePropDefs(),
                caption: {
                    type: PropType.string,
                    visibleInMcLeodTailor: true,
                    editableInMcLeodTailor: true,
                    description: "The text to display in the tab's heading."
                },
                closeable: {
                    type: PropType.bool,
                    defaultValue: false,
                    description: "Specifies if the tab can be closed by the user."
                },
                displaysDividerBorder: {
                    type: PropType.bool,
                    defaultValue: false,
                    description: "Specifies if the tab should render a divider border at the bottom of the tab selector.  Note that this only affects tabsets with a style of 'underlined' and a tab alignment of 'left' or 'right'.  Also, the last tab in the tabset will never display the divider border."
                },
                headingMaxWidth: {
                    type: PropType.number,
                    description: "The maximum width of the tab's heading.  If not specified, the width will be determined by the length of the caption.  Note that this setting is used only when the tab headings are displayed above or below the tab content."
                },
                headingMinWidth: {
                    type: PropType.number,
                    defaultValue: 160,
                    description: "The minimum width of the tab's heading.  If not specified, the width will be determined by the length of the caption.  Note that this setting is used only when the tab headings are displayed above or below the tab content."
                },
                showTitleInScroll: {
                    type: PropType.bool,
                    defaultValue: true,
                    description: "Specifies if the tab, when rendered in scroll mode, should display a title label whose caption matches the tab's heading caption."
                },
                beforeSelection: {
                    type: PropType.event,
                    category: "Events",
                    addListenerMethod: "addBeforeSelectionListener",
                    removeListenerMethod: "removeBeforeSelectionListener",
                    description: "This event is fired before a Tab is selected."
                },
                afterSelection: {
                    type: PropType.event,
                    category: "Events",
                    addListenerMethod: "addAfterSelectionListener",
                    removeListenerMethod: "removeAfterSelectionListener",
                    description: "This event is fired after a Tab is selected."
                },
                beforeClose: {
                    type: PropType.event,
                    category: "Events",
                    addListenerMethod: "addBeforeCloseListener",
                    removeListenerMethod: "removeBeforeCloseListener",
                    description: "This event is fired before a tab is closed/removed from the Tabset."
                },
                afterClose: {
                    type: PropType.event,
                    category: "Events",
                    addListenerMethod: "addAfterCloseListener",
                    removeListenerMethod: "removeAfterCloseListener",
                    description: "This event is fired after a tab is closed/removed from the Tabset."
                }
            };
            ComponentPropDefinitionUtil.populateComponentPropNames(tabPropDefs);
            tabPropDefs.tooltip.description = "Defines a message to display to the user when they hover over the tab's header/selector.  It may be overridden by tooltips that are configured programmatically.";
        }
        return tabPropDefs;
    }
}
